import { DestroyRef, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import moment, { isMoment } from 'moment';
import { BehaviorSubject, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { components, DATE_DAY, DATE_FROM, DATE_TO, DATE_WEEK } from '../const/globals.const';
import { roomBufferPrograms } from '../interfaces/buffer-program.interfaces';
import { BufferProgram } from '../models/buffer-program.model';
import { Room } from '../models/room.model';
import { getFirstHospitalSelectedData } from '../utils/cross-functions';
import { PopupManagerService } from './popup-manager.service';
import { SubscriptionManager } from './subscription-manager.service';
import { ToastService } from './toast.service';
import { UserService } from './user.service';
import { InstanceIdService } from '@shared/services/instance-id.service';

@Injectable({
  providedIn: 'root',
})
export class UtilisService {
  private instanceId: string;

  private sidenavToggleSubject = new Subject<void>();

  public isSideNavReduced: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  public sidenavToggle$ = this.sidenavToggleSubject.asObservable();

  toggleSidenav() {
    setTimeout(() => {
      this.sidenavToggleSubject.next();
    }, 1);
  }

  getTodayDate() {
    const d = new Date();
    let month = '' + (d.getMonth() + 1);
    let day = '' + d.getDate();
    const year = d.getFullYear();

    if (month.length < 2) {
      month = '0' + month;
    }
    if (day.length < 2) {
      day = '0' + day;
    }

    return [year, month, day].join('-');
  }

  public isFullscreen: boolean = false;

  private DEFAULT_SUCCESS_MSG = "L'opération a été prise en compte";

  private DEFAULT_ERROR_MSG = "L'opération a échoué";

  constructor(
    private userService: UserService,
    private toastService: ToastService,
    private dialog: MatDialog,
    private popupManagerService: PopupManagerService,
    private subscriptionManager: SubscriptionManager,
    private destroyRef: DestroyRef,
    private instanceIdService: InstanceIdService
  ) {
    this.instanceId = this.instanceIdService.generateId();
    this.subscriptionManager.registerComponent(this.instanceId, this.destroyRef);
  }

  moveFirstItemToLastPosition(array: any[]) {
    const firstItem = array[0];
    array.splice(0, 1);
    array.push(firstItem);
  }

  reverse(forwardString: string): string {
    return forwardString.split('-').reverse().join('-');
  }

  getMorningPrograms(room: Room): BufferProgram[] {
    const selectedHospital = getFirstHospitalSelectedData(
      this.userService.getCurrentUser(),
      this.userService.getSelectedHospitals()
    );
    const hospitalStartTimeString = this.getStartTime(selectedHospital?.startTime);
    const hospitalStartTimeNumber = parseInt(hospitalStartTimeString.replace(':', ''), 10);

    const hospitalMiddleTimeString = this.getStartTime(selectedHospital?.middleTime);
    const hospitalMiddleTimeNumber = parseInt(hospitalMiddleTimeString.replace(':', ''), 10);
    return (
      room.programs?.filter((program: BufferProgram) => {
        const startTimeString = this.getStartTime(program.startTime);
        const startTimeNumber = parseInt(startTimeString.replace(':', ''), 10);

        const endTimeString = this.getStartTime(program.endTime);
        const endTimeNumber = parseInt(endTimeString.replace(':', ''), 10);
        return (
          startTimeNumber >= hospitalStartTimeNumber &&
          startTimeNumber <= hospitalMiddleTimeNumber &&
          endTimeNumber <= hospitalMiddleTimeNumber
        );
      }) ?? []
    );
  }

  getDayPrograms(room: Room): BufferProgram[] {
    const selectedHospital = getFirstHospitalSelectedData(
      this.userService.getCurrentUser(),
      this.userService.getSelectedHospitals()
    );

    const hospitalMiddleTimeString = this.getStartTime(selectedHospital?.middleTime);
    const hospitalMiddleTimeNumber = parseInt(hospitalMiddleTimeString.replace(':', ''), 10);
    return (
      room.programs?.filter((program: BufferProgram) => {
        const startTimeString = this.getStartTime(program.startTime);
        const startTimeNumber = parseInt(startTimeString.replace(':', ''), 10);

        const endTimeString = this.getStartTime(program.endTime);
        const endTimeNumber = parseInt(endTimeString.replace(':', ''), 10);
        return startTimeNumber < hospitalMiddleTimeNumber && endTimeNumber > hospitalMiddleTimeNumber;
      }) ?? []
    );
  }

  getAfternoonPrograms(room: Room): BufferProgram[] {
    const selectedHospital = getFirstHospitalSelectedData(
      this.userService.getCurrentUser(),
      this.userService.getSelectedHospitals()
    );

    const hospitalEndTimeString = this.getStartTime(selectedHospital?.endTime);
    const hospitalEndTimeNumber = parseInt(hospitalEndTimeString.replace(':', ''), 10);

    const hospitalMiddleTimeString = this.getStartTime(selectedHospital?.middleTime);
    const hospitalMiddleTimeNumber = parseInt(hospitalMiddleTimeString.replace(':', ''), 10);
    return (
      room.programs?.filter((program: BufferProgram) => {
        const startTimeString = this.getStartTime(program.startTime);
        const startTimeNumber = parseInt(startTimeString.replace(':', ''), 10);

        const endTimeString = this.getStartTime(program.endTime);
        const endTimeNumber = parseInt(endTimeString.replace(':', ''), 10);
        return (
          startTimeNumber >= hospitalMiddleTimeNumber &&
          startTimeNumber <= hospitalEndTimeNumber &&
          endTimeNumber <= hospitalEndTimeNumber
        );
      }) ?? []
    );
  }

  getStartTime(date: any): string {
    const d = new Date(date);
    d.setTime(d.getTime() + d.getTimezoneOffset() * 60 * 1000);
    return `${d.getHours()}:${d.getMinutes() === 0 ? '00' : d.getMinutes()}`;
  }

  isBPsHaveSameTeam(room: roomBufferPrograms, so?: boolean): boolean {
    if (!room.programs) {
      return false;
    }
    if (room.programs.length === 1) {
      return true;
    }

    return this.isBPsHaveSameProfil(room, 'nurses', so) && this.isBPsHaveSameProfil(room, 'anesthetist');
  }

  isBPsHaveSameProfil(room: any, prop: string, so?: boolean): boolean {
    let result = true;
    let leastOne = false;

    if (so) {
      room.surgeonOpenings.forEach((p: any) => {
        result = !p[prop] && result;
      });
      if (result) {
        return result;
      }
    } else {
      room.programs.forEach((p: any) => {
        result = !p[prop] && result;
      });
      if (result) {
        return result;
      }
    }

    result = true;
    if (prop === 'nurses') {
      if (so) {
        const nurses = room.surgeonOpenings[0].nurses;
        room.surgeonOpenings.forEach((p: any) => {
          if (p.nurses.length != nurses.length) {
            result = false;
          }
          p.nurses.forEach((nurse: any) => {
            if (nurse && nurse.profile) {
              const p2 = nurses.find((nurse_: any) => String(nurse_.profile._id) === String(nurse.profile._id));
              if (p2 && p2.role) {
                result = p2 && String(p2.role) === String(nurse.role) && result;
                leastOne = p2 && String(p2.role) !== String(nurse.role);
              } else {
                result = false;
              }
            } else {
              result = false;
            }
          });
        });
      } else {
        const nurses = room.programs[0].nurses;
        room.programs.forEach((p: any) => {
          if (p.nurses.length != nurses.length) {
            result = false;
          }

          p.nurses.forEach((nurse: any) => {
            if (nurse && nurse.profile) {
              const p2 = nurses.find((nurse_: any) => String(nurse_.profile._id) === String(nurse.profile._id));
              if (p2 && p2.role && nurse.role) {
                result = p2 && String(p2.role._id) === String(nurse.role._id) && result;
                leastOne = p2 && String(p2.role) !== String(nurse.role);
              } else {
                result = false;
              }
            } else {
              result = false;
            }
          });
        });
      }
    } else {
      const profil = room.programs[0][prop];
      room.programs.forEach((p: any) => {
        if (p[prop] && profil) {
          result = p[prop]._id == profil._id && result;
        } else {
          result = false;
        }
      });
    }

    return leastOne ? !leastOne : result;
  }

  displaySuccessToast(message?: string) {
    this.toastService.infoToast(message ? message : this.DEFAULT_SUCCESS_MSG);
  }

  displayErrorToast(message?: string) {
    this.toastService.errorToast(message ? message : this.DEFAULT_ERROR_MSG);
  }

  openPopup(component: any, config: any): any {
    const dialogRef = this.dialog.open(component, config);
    return dialogRef;
  }

  openUnSavedChangesPopup() {
    return this.subscriptionManager
      .createRequest(
        this.popupManagerService.openUnsavedChangesPopup(false).afterClosed(),
        'openUnsavedChangesPopup',
        this.instanceId
      )
      .pipe(
        map((result: string) => {
          switch (result) {
            case 'save':
              return false;
            case 'cancel':
              return 'cancel';
            case 'continue':
              return true;
            default:
              return 'cancel';
          }
        })
      );
  }

  openUnSavedCommentPopup() {
    return this.subscriptionManager
      .createRequest(
        this.popupManagerService.openUnsavedCommentPopup(false).afterClosed(),
        'openUnsavedCommentPopup',
        this.instanceId
      )
      .pipe(
        map((result: string) => {
          switch (result) {
            case 'save':
              return false;
            case 'cancel':
              return 'cancel';
            case 'continue':
              return true;
            default:
              return 'cancel';
          }
        })
      );
  }

  saveDate(component: string, day: any) {
    let formattedDate = day;
    if (day) {
      if (isMoment(day)) {
        formattedDate = day.format('YYYY-MM-DD');
      } else {
        const d = new Date(day);
        formattedDate = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
      }
    } else {
      const d = new Date();
      formattedDate = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
    }
    localStorage.setItem(this.getKey(component), formattedDate);
  }

  getDate(component: string) {
    const savedDate = this.getSavedDate(this.getKey(component));
    if (components.RECAP_COMPONENT == component) {
      return savedDate;
    }

    return savedDate ? savedDate : this.getCurrentDate();
  }

  getCurrentDate() {
    const momentDate: moment.Moment = moment.utc();
    return momentDate.clone();
  }

  getKey(component: string) {
    if (
      component == components.INFIRMIER_COMPONENT ||
      component == components.SURGEON_COMPONENT ||
      component == components.ANE_COMPONENT ||
      component == components.SP_COMPONENT
    ) {
      return DATE_WEEK;
    } else {
      return DATE_DAY;
    }
  }

  getSavedDate(varName: string) {
    const savedDate = localStorage.getItem(varName);
    if (savedDate && savedDate != 'undefined' && savedDate != 'Invalid date') {
      return moment.utc(new Date(localStorage.getItem(varName) ?? ''));
    }
    return null;
  }

  openConfirmationPopup(component: any, message: string, config?: any) {
    const dialogRef = this.dialog.open(component, {
      width: '320px',
      height: config.height ? config.height : '120px',
      panelClass: config.panelClass,
      data: {
        cssClass: 'danger',
        message: message,
        title: config.title,
      },
    });
    return dialogRef;
  }

  getDateFromTo(isSameDay?: boolean) {
    const savedFromDate = this.getSavedDate(DATE_FROM);
    const savedToDate = this.getSavedDate(DATE_TO);
    let defaultFromDate;
    if (isSameDay) {
      defaultFromDate = this.getCurrentDate();
    } else {
      defaultFromDate = this.getCurrentDate().add(1, 'd');
    }
    const defaultToDate = this.getCurrentDate().add(8, 'd');
    return savedFromDate && savedToDate
      ? { fromDate: savedFromDate, toDate: savedToDate }
      : { fromDate: defaultFromDate, toDate: defaultToDate };
  }

  saveFromToDate(fromDate: any, toDate: any) {
    const formattedFromDate = moment(fromDate).format('YYYY-MM-DD');
    const formattedToDate = moment(toDate).format('YYYY-MM-DD');
    localStorage.setItem(DATE_FROM, formattedFromDate);
    localStorage.setItem(DATE_TO, formattedToDate);
  }

  formatDate2(date: string | Date, type?: boolean) {
    const d = new Date(date);
    let month = '' + (d.getMonth() + 1);
    let day = '' + d.getDate();
    const year = d.getFullYear();

    if (month.length < 2) {
      month = '0' + month;
    }
    if (day.length < 2) {
      day = '0' + day;
    }

    if (type) {
      return [day, month, year].join('-');
    }
    return [year, month, day].join('-');
  }

  getDayWithoutTime(d: Date) {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }

  getMonday(d: string | Date) {
    d = this.getDayWithoutTime(new Date(d));
    const day = d.getDay(),
      diff = d.getDate() - day + (day == 0 ? -6 : 1); // adjust when day is sunday
    return new Date(d.setDate(diff));
  }

  getSunday(d: string | Date) {
    d = this.getDayWithoutTime(new Date(d));
    const day = d.getDay(),
      diff = d.getDate() + (day == 0 ? 0 : 7 - day); // adjust when day is sunday
    return new Date(d.setDate(diff));
  }

  getBase64ImageFromURL(url: string): Promise<string> {
    return new Promise((resolve: any, reject: any) => {
      const img = new Image();
      img.setAttribute('crossOrigin', 'anonymous');
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(img, 0, 0);
        }
        const dataURL = canvas.toDataURL('image/png');
        resolve(dataURL);
      };
      img.onerror = (error: string | Event) => {
        reject(error);
      };
      img.src = url;
    });
  }

  clear() {
    this.sidenavToggleSubject.complete();
    this.sidenavToggleSubject = new Subject<void>();

    this.isSideNavReduced.complete();
    this.isSideNavReduced = new BehaviorSubject<boolean>(false);

    this.sidenavToggle$ = this.sidenavToggleSubject.asObservable();

    this.isFullscreen = false;
  }

  getLocalDate(name: string) {
    return JSON.parse(localStorage.getItem(name) ?? 'null');
  }

  setLocalDate(date: any) {
    localStorage.setItem('vue_hebdo_date', JSON.stringify(date));
  }
}
